/**
 * menuIcons.tsx — the small row glyphs for the Menu Bar profile dropdown.
 *
 * Ported the SAME way as icons.tsx (see that file's header): the path geometry is
 * lifted VERBATIM from the exported SVGs of the "Menu bar Dropdown" frame
 * (Figma GaB9IJrgGQf6V8uI4WBYIu, node 7:408), one glyph per dropdown row. The
 * exports are drawn at ~7px in #161616; here the fill is `currentColor` so each
 * glyph inherits the row's text colour (the icons.tsx convention — a literal fill
 * would also fail audit-tokens.mjs), and the viewBox is kept at the source's own
 * dimensions so the geometry can be diffed against the frame without re-origining.
 *
 * These are IBM-Carbon-style line icons. `size` defaults to the frame's drawn 14px
 * box (the dropdown draws a 7px glyph in a 7px box, but at the dropdown's real
 * 11px text a 14px icon reads correctly beside it); callers scale via `size`.
 */

interface IconProps {
  className?: string;
  /** Rendered size in px (square). */
  size?: number;
}

/** Person head-and-shoulders — "View Kombi Profile". Source 5x7, padded to a 7-wide box. */
export function ProfileGlyph({ className = '', size = 14 }: IconProps) {
  return (
    <svg
      viewBox="0 0 7 7"
      width={size}
      height={size}
      className={className}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <g transform="translate(1 0)">
        <path d="M2.5 0.5C2.74723 0.5 2.9889 0.573311 3.19446 0.710663C3.40002 0.848015 3.56024 1.04324 3.65485 1.27165C3.74946 1.50005 3.77421 1.75139 3.72598 1.99386C3.67775 2.23634 3.5587 2.45907 3.38388 2.63388C3.20907 2.8087 2.98634 2.92775 2.74386 2.97598C2.50139 3.02421 2.25005 2.99946 2.02165 2.90485C1.79324 2.81024 1.59801 2.65002 1.46066 2.44446C1.32331 2.2389 1.25 1.99723 1.25 1.75C1.25 1.41848 1.3817 1.10054 1.61612 0.866117C1.85054 0.631696 2.16848 0.5 2.5 0.5ZM2.5 0C2.15388 0 1.81554 0.102636 1.52775 0.294928C1.23997 0.487221 1.01566 0.760533 0.883211 1.0803C0.750758 1.40007 0.716102 1.75194 0.783626 2.09141C0.85115 2.43087 1.01782 2.74269 1.26256 2.98744C1.50731 3.23218 1.81913 3.39885 2.15859 3.46637C2.49806 3.5339 2.84993 3.49924 3.1697 3.36679C3.48947 3.23434 3.76278 3.01003 3.95507 2.72225C4.14736 2.43446 4.25 2.09612 4.25 1.75C4.25 1.28587 4.06563 0.840752 3.73744 0.512563C3.40925 0.184374 2.96413 0 2.5 0Z" />
        <path d="M5 7H4.5V5.75C4.5 5.58585 4.46767 5.4233 4.40485 5.27165C4.34203 5.11999 4.24996 4.98219 4.13388 4.86612C4.01781 4.75004 3.88001 4.65797 3.72835 4.59515C3.5767 4.53233 3.41415 4.5 3.25 4.5H1.75C1.41848 4.5 1.10054 4.6317 0.866117 4.86612C0.631696 5.10054 0.5 5.41848 0.5 5.75V7H0V5.75C0 5.28587 0.184375 4.84075 0.512563 4.51256C0.840752 4.18437 1.28587 4 1.75 4H3.25C3.71413 4 4.15925 4.18437 4.48744 4.51256C4.81563 4.84075 5 5.28587 5 5.75V7Z" />
      </g>
    </svg>
  );
}

/** Bookmark tag — "Saved Tools". Source 4.375x6.125, padded to a 7-wide box. */
export function BookmarkGlyph({ className = '', size = 14 }: IconProps) {
  return (
    <svg
      viewBox="0 0 7 7"
      width={size}
      height={size}
      className={className}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <g transform="translate(1.3 0.4)">
        <path d="M3.9375 0.4375V5.41406L2.38437 4.62875L2.1875 4.53031L1.99063 4.62875L0.4375 5.41406V0.4375H3.9375ZM3.9375 0H0.4375C0.321468 0 0.210188 0.0460936 0.128141 0.128141C0.0460936 0.210188 0 0.321468 0 0.4375V6.125L2.1875 5.03125L4.375 6.125V0.4375C4.375 0.321468 4.32891 0.210188 4.24686 0.128141C4.16481 0.0460936 4.05353 0 3.9375 0Z" />
      </g>
    </svg>
  );
}

/** Two-column grid — "My Learning". Source 7x7. */
export function GridGlyph({ className = '', size = 14 }: IconProps) {
  return (
    <svg
      viewBox="0 0 7 7"
      width={size}
      height={size}
      className={className}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M6 1.59091H4.25V2.22727H6V1.59091Z" />
      <path d="M6 3.18182H4.25V3.81818H6V3.18182Z" />
      <path d="M6 4.77273H4.25V5.40909H6V4.77273Z" />
      <path d="M2.75 1.59091H1V2.22727H2.75V1.59091Z" />
      <path d="M2.75 3.18182H1V3.81818H2.75V3.18182Z" />
      <path d="M2.75 4.77273H1V5.40909H2.75V4.77273Z" />
      <path d="M6.5 0H0.5C0.367432 0.00016847 0.240332 0.0672679 0.146593 0.186573C0.0528533 0.305878 0.000132369 0.467641 0 0.636364V6.36364C0.000132369 6.53236 0.0528533 6.69412 0.146593 6.81343C0.240332 6.93273 0.367432 6.99983 0.5 7H6.5C6.63257 6.99983 6.75967 6.93273 6.85341 6.81343C6.94715 6.69412 6.99987 6.53236 7 6.36364V0.636364C6.99987 0.467641 6.94715 0.305878 6.85341 0.186573C6.75967 0.0672679 6.63257 0.00016847 6.5 0ZM0.5 0.636364H3.25V6.36364H0.5V0.636364ZM3.75 6.36364V0.636364H6.5V6.36364H3.75Z" />
    </svg>
  );
}

/** Reply / chat bubble — "Kombi Forum". Source 7x7. */
export function ForumGlyph({ className = '', size = 14 }: IconProps) {
  return (
    <svg
      viewBox="0 0 7 7"
      width={size}
      height={size}
      className={className}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M6.5 1H1.5C1.2 1 1 1.2 1 1.5V5C1 5.3 1.2 5.5 1.5 5.5H3.5V5H1.5V1.5H6.5V5H4.7L3.5 6.7L3.9 7L4.95 5.5H6.5C6.8 5.5 7 5.3 7 5V1.5C7 1.2 6.8 1 6.5 1Z" />
      <path d="M0.5 4H0V0.75C0 0.325 0.325 0 0.75 0H4V0.5H0.75C0.6 0.5 0.5 0.6 0.5 0.75V4Z" />
    </svg>
  );
}
